import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/** Status referral (kode unik, jumlah undangan, jumlah undangan sukses) milik akun yang masuk. */
export const getMyReferral = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ idToken: z.string().min(10) }).parse(d))
  .handler(async ({ data }) => {
    const { verifyIdToken } = await import("./admin-auth.server");
    const email = await verifyIdToken(data.idToken);
    if (!email) throw new Error("Sesi tidak valid. Silakan masuk kembali dengan Google.");
    const { getReferralStatus } = await import("./db.server");
    return getReferralStatus(email);
  });

/** Mencatat bahwa akun yang baru masuk datang lewat kode referral tertentu. */
export const claimReferral = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ idToken: z.string().min(10), code: z.string().min(3).max(24) }).parse(d),
  )
  .handler(async ({ data }) => {
    const { verifyIdToken } = await import("./admin-auth.server");
    const email = await verifyIdToken(data.idToken);
    if (!email) return { ok: false } as const;
    const { recordReferralSignup } = await import("./db.server");
    return recordReferralSignup(data.code, email);
  });

/** Ringkasan sumber traffic hasil aksi bagikan & widget untuk listing milik akun. */
export const getMyTraffic = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ idToken: z.string().min(10), days: z.number().int().min(1).max(365).default(30) }).parse(d),
  )
  .handler(async ({ data }) => {
    const { verifyIdToken } = await import("./admin-auth.server");
    const email = await verifyIdToken(data.idToken);
    if (!email) throw new Error("Sesi tidak valid. Silakan masuk kembali dengan Google.");
    const { fetchMyTraffic } = await import("./db.server");
    return fetchMyTraffic(email, data.days);
  });

/** Analitik detail (filter sumber/campaign, breakdown harian, varian OG, baris CSV). */
export const getMyAnalytics = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        idToken: z.string().min(10),
        days: z.number().int().min(1).max(365).default(30),
        source: z.string().max(40).optional(),
        campaign: z.string().max(60).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { verifyIdToken } = await import("./admin-auth.server");
    const email = await verifyIdToken(data.idToken);
    if (!email) throw new Error("Sesi tidak valid. Silakan masuk kembali dengan Google.");
    const { fetchMyAnalytics, fetchMyAnalyticsFilters } = await import("./db.server");
    const [detail, filters] = await Promise.all([
      fetchMyAnalytics(email, { days: data.days, source: data.source, campaign: data.campaign }),
      fetchMyAnalyticsFilters(email),
    ]);
    return { ...detail, filters };
  });

/** Notifikasi pertumbuhan (mis. undangan referral yang berhasil). */
export const getMyGrowthNotifications = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ idToken: z.string().min(10) }).parse(d))
  .handler(async ({ data }) => {
    const { verifyIdToken } = await import("./admin-auth.server");
    const email = await verifyIdToken(data.idToken);
    if (!email) throw new Error("Sesi tidak valid. Silakan masuk kembali dengan Google.");
    const { fetchGrowthNotifications } = await import("./db.server");
    return fetchGrowthNotifications(email, 10);
  });

/** Leaderboard referral publik (nama disamarkan). */
export const getReferralLeaderboard = createServerFn({ method: "GET" }).handler(async () => {
  const { fetchReferralLeaderboard } = await import("./db.server");
  try {
    return await fetchReferralLeaderboard(20);
  } catch {
    return [];
  }
});

/** Mencatat event bagikan/embed (publik, tanpa data pribadi). */
export const trackShare = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        slug: z.string().max(120).default(""),
        event: z.string().min(2).max(40),
        source: z.string().max(40).default("unknown"),
        medium: z.string().max(40).default("unknown"),
        campaign: z.string().max(60).default("unknown"),
        variant: z.string().max(12).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { getRequest } = await import("@tanstack/react-start/server");
    const { logShareEvent } = await import("./db.server");
    let ip = "unknown";
    try {
      const req = getRequest();
      ip =
        req.headers.get("cf-connecting-ip") ??
        req.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ??
        "unknown";
    } catch {
      /* di luar konteks request */
    }
    try {
      return await logShareEvent({ ...data, ip });
    } catch {
      return { ok: false } as const;
    }
  });


/** Preferensi notifikasi email milik akun yang masuk. */
export const getMyEmailPref = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ idToken: z.string().min(10) }).parse(d))
  .handler(async ({ data }) => {
    const { verifyIdToken } = await import("./admin-auth.server");
    const email = await verifyIdToken(data.idToken);
    if (!email) throw new Error("Sesi tidak valid. Silakan masuk kembali dengan Google.");
    const { isEmailNotificationsOn } = await import("./db.server");
    return { enabled: await isEmailNotificationsOn(email) };
  });

/** Mengaktifkan atau menonaktifkan notifikasi email. */
export const setMyEmailPref = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ idToken: z.string().min(10), enabled: z.boolean() }).parse(d),
  )
  .handler(async ({ data }) => {
    const { verifyIdToken } = await import("./admin-auth.server");
    const email = await verifyIdToken(data.idToken);
    if (!email) throw new Error("Sesi tidak valid. Silakan masuk kembali dengan Google.");
    const { setEmailNotifications } = await import("./db.server");
    return setEmailNotifications(email, data.enabled);
  });
